import { useEffect, useState } from 'react';
import OverlayLoader from '../../../../../components/overlayLoader';
import LoanCardList from '../components/LoanCardList';
import { resellerStatusPerLoanType } from '../utils';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../../../../services/auth/AuthContext';

import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import fetchApplicationsService from '../../../../../services/api/fetchApplications';

export default function RejectedApplications({ loanType, setCurrentStatusCode }) {
    const { t } = useTranslation();

    const { setIsAuthenticated } = useAuth();
    const navigate = useNavigate();

    const resellerStatusValuesRejected = resellerStatusPerLoanType[loanType]['rejected_loans'] || resellerStatusPerLoanType[loanType]['in_progress'];

    const [searchParams] = useSearchParams();

    // With the status strip gone from this section there is nothing left to
    // switch the code, and pinning latest_status to the first one hid every
    // lead closed at any other step. The section lists the whole bucket and is
    // scoped by product instead - except the no-loan-type bucket, which has a
    // single status and no product code to send.
    const isChatInitiated = loanType === 'null';
    const latestStatus = isChatInitiated ? Object.keys(resellerStatusValuesRejected)[0] : '';
    const source = searchParams.get('source') ?? 'all';
    const campaign = searchParams.get('campaign') ?? 'all';
    const state = searchParams.get('state') ?? 'all';
    const priority = searchParams.get('priority') ?? 'all';
    const dispositions = searchParams.get('dispositions') ?? 'all';
	const sort = searchParams.get('sort') ?? 'default';
	const rejectionReason = searchParams.get('rejectionReason') ?? 'all';

    const [applications, setApplications] = useState([]);
    const [resellerData, setResellerData] = useState({});
    const [loading, setLoading] = useState(false);
	const [hasMore, setHasMore] = useState(true);
	const [cursor, setCursor] = useState(null);

	const fetchLoanApplicationsData = async (isNewSearch = false, isLoading = true) => {
		try {
			setLoading(isLoading);
			const isInitialLoad = isNewSearch || applications.length === 0;
			// The cursor is the backend's, not ours: under a sort other than
			// newest-id-first, the last row's id alone does not identify the page
			// boundary, so the pair it returns has to come back verbatim.
			const pageCursor = isNewSearch ? null : cursor;

			const response = await fetchApplicationsService.getApplicationsForStatusCode(
				latestStatus,
				{
					...(isChatInitiated ? {} : { loan_type_code: loanType }),
					source: source,
					campaign: campaign,
					state: state,
					priority: priority,
					dispositions: dispositions,
                    rejection_reason: rejectionReason,
					sort: sort,
					...(pageCursor && {
					    last_id: pageCursor.last_id,
					    last_sort_value: pageCursor.last_sort_value ?? '',
					}),
					limit: 5,
                    current_application_status: 'rejected',
				},
				setIsAuthenticated,
				navigate
			);

			if (isInitialLoad) {
				setApplications(response.applications || []);
			} else {
				setApplications(prevApplications => [...prevApplications, ...(response.applications || [])]);
			}

			setResellerData((prev) => ({
				...prev,
				...response.reseller_data,
			}));
			setHasMore(response.has_more);
			setCursor(response.next_cursor ?? null);
		} catch (error) {
			console.log(error);
		} finally {
			setLoading(false);
		}
	};

    useEffect(() => {
		setApplications([]);
		setCursor(null);
		setHasMore(true);
		fetchLoanApplicationsData(true);
	}, [latestStatus, loanType, source, campaign, state, priority, dispositions, rejectionReason, sort]);

    return (
        <Box
            display={'flex'}
            flexDirection={'column'}
            height={'100%'}
            overflow={'auto'}
        >
            <OverlayLoader
                open={loading}
                text={t('loading.rejected') + '...'}
            />

            {applications ? (
                <>
                    <LoanCardList
                        applications={applications}
                        setApplications={setApplications}
                        currentStatusCode={isChatInitiated ? latestStatus : null}
                        allStatus={isChatInitiated ? resellerStatusValuesRejected : null}
                        resellerData={resellerData}
                        loanType={loanType}
                        loadMore={() => fetchLoanApplicationsData(false, false)}
                        hasMore={hasMore}
                        displayRejectionReasonFilter
                        showStepper={false}
                    />
                </>
            ) : (
                <>
                    <Typography>No data available</Typography>
                </>
            )}
        </Box>
    );
}
